import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import classNames from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { IAPIResponse, StatusCode } from "api/protocols";
import { UserActive } from "api/rpc";
import { GetReservationModule, GetReservationSegment } from "api/rpc/guest/reservations/reservations";
import Callout from "components/callout/Callout";
import Page from "components/page/Page";
import PortalDatePickerInput from "elements/teesheet/PortalDatePickerInput";
import DateBar from "elements/teesheet/DateBar";

import { IUIActions, showError } from "redux/actions/ui";
import { ILocation, IModule, ISegment } from "redux/reducers/models/reservations";
import { IReservationActions } from "redux/actions/reservations";
import "./reservations.scss";
import { ReservationsNavigation } from "./ReservationsNavigation/ReservationsNavigation";
import { IReservationState as IReservationStore } from "redux/reducers/reservations";
import ReactDOM from "react-dom";
import { capitalize, formatDate } from "helpers/Helpers";
import { GetFacility } from "api/rpc/guest/facility";
import { LocaleCurrency } from "helpers/Locale";
import { segmentAvailabilityPrice } from "helpers/segmentHourlyPrice";
import { GetCustomerReservationSegment } from "api/rpc/facilityAdmin/customer/reservations";
import axios, { CancelToken } from "axios";
import {
  GetCustomerReservationNotificationAll,
  GetGuestReservationNotificationAll,
  IReservationNotification,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { useAppDispatch } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { getPortalDateBarShowNavigation } from "helpers/portalDateControls";
import { GetGuestReservationLocation } from "api/rpc/2024-04/guest/reservation";
interface IProps {
  uiActions: IUIActions;
  reservationActions: IReservationActions;
  reservationStore: IReservationStore;
}

interface IParams {
  facilityShortName: string;
  moduleHandle: string;
}

interface IReservationState {
  module: IModule;
  locations: Array<ILocation>;
  segments: { [key: string]: Array<ISegment> };
  selectedDate: Date;
  activeUser: boolean;
  notifications: IReservationNotification[];
}

export default function Reservations(props: IProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const { uiActions, reservationActions, reservationStore } = props;
  const { facilityShortName, moduleHandle } = useParams<IParams>();
  const [reservationState, setReservationState] = useState<IReservationState>({
    module: null,
    segments: null,
    locations: null,
    selectedDate: new Date(),
    activeUser: false,
    notifications: [],
  });
  const history = useHistory();

  useEffect(() => {
    void getModule();
  }, []);

  useEffect(() => {
    if (reservationState.module) {
      void GetSegment();
    }
  }, [reservationState.selectedDate]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (reservationState.selectedDate && reservationState.module) {
      void loadReservationNotifications(reservationState.selectedDate, source.token);
    }
    return () => source.cancel();
  }, [reservationState.selectedDate, reservationState.module]);

  async function getModule() {
    uiActions.enqueue();
    const activeUserRes = await UserActive(false);

    if (activeUserRes.status === StatusCode.OK) {
      reservationActions.update({ active_user: activeUserRes?.data });
      setReservationState(prevState => ({
        ...prevState,
        activeUser: true,
      }));
    }

    const moduleRes = await GetReservationModule(
      { facility_short_name: facilityShortName, handle: moduleHandle },
      false,
    );

    if (moduleRes.status !== StatusCode.OK) {
      uiActions.showError(t("Error getting module"));
      uiActions.dequeue();
      return;
    }

    if (!reservationStore?.facility) {
      const facilityRes = await GetFacility({ id: moduleRes?.data?.facility_id }, false);
      if (facilityRes?.status !== StatusCode.OK) {
        uiActions.showError(t("Error getting facility"));
        uiActions.dequeue();
        return;
      }
      reservationActions.update({ facility: facilityRes?.data[0] });
    }

    const locationRes = await GetGuestReservationLocation(
      { facility_id: moduleRes.data?.facility_id, reservation_module_id: moduleRes?.data?.id },
      false,
    );
    if (locationRes.status !== StatusCode.OK) {
      uiActions.showError(t("Error getting location"));
      uiActions.dequeue();
      return;
    }

    let segmentRes;

    //Call the Customer GET Segments endpoint if the customer is logged in, else call Guest endpoint
    if (activeUserRes.status === StatusCode.OK) {
      segmentRes = await GetCustomerReservationSegment(
        { module_id: moduleRes.data?.id, date: moment(reservationState.selectedDate).format("YYYY-MM-DD") },
        false,
      );
      if (segmentRes.status !== StatusCode.OK) {
        uiActions.showError(t("Reservations unavailable"));
        setReservationState(prevState => ({ ...prevState, segments: null }));
      }
    } else {
      segmentRes = await GetReservationSegment(
        { module_id: moduleRes.data?.id, date: moment(reservationState.selectedDate).format("YYYY-MM-DD") },
        false,
      );
      if (segmentRes.status !== StatusCode.OK) {
        uiActions.showError(t("Reservations unavailable"));
        setReservationState(prevState => ({ ...prevState, segments: null }));
      }
    }

    uiActions.dequeue();

    const formattedSegments = segmentRes.data?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );

    ReactDOM.unstable_batchedUpdates(() => {
      reservationActions.update({ module: moduleRes.data, selectedSegment: null, selectedLocation: null });
      setReservationState(prevState => ({
        ...prevState,
        module: moduleRes.data,
        segments: formattedSegments,
        locations: locationRes.data,
      }));
    });
  }

  async function GetSegment() {
    let segmentRes;
    //Call the Customer GET Segments endpoint if the customer is logged in, else call Guest endpoint
    if (reservationState.activeUser) {
      segmentRes = await GetCustomerReservationSegment(
        { module_id: reservationState?.module?.id, date: moment(reservationState.selectedDate).format("YYYY-MM-DD") },
        true,
      );
      if (segmentRes.status !== StatusCode.OK) {
        uiActions.showError(t("Reservations unavailable"));
        setReservationState(prevState => ({ ...prevState, segments: null }));
        return;
      }
    } else {
      segmentRes = await GetReservationSegment(
        {
          module_id: reservationState?.module?.id,
          date: moment(reservationState.selectedDate).format("YYYY-MM-DD"),
        },
        true,
      );
      if (segmentRes.status !== StatusCode.OK) {
        uiActions.showError(t("Reservations unavailable"));
        setReservationState(prevState => ({ ...prevState, segments: null }));
        return;
      }
    }

    const formattedSegments = segmentRes.data?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );

    setReservationState(prevState => ({ ...prevState, segments: formattedSegments }));
  }

  function handleDateChange(selectedDate: Date) {
    setReservationState(prevState => ({ ...prevState, selectedDate }));
  }

  function checkUserLoggedIn(location: ILocation, segment: ISegment) {
    reservationActions.update({ selectedSegment: segment, selectedLocation: location });
    if (reservationState.activeUser) {
      history.push(
        `/${facilityShortName}/${moduleHandle}/booking?start_time=${segment?.start_time}&date=${segment?.date}&location_id=${location?.id}`,
      );
    } else {
      history.push(
        `/${facilityShortName}/${moduleHandle}/login/?start_time=${segment?.start_time}&date=${segment?.date}&location_id=${location?.id}`,
      );
    }
  }

  async function loadReservationNotifications(date: Date, token?: CancelToken) {
    // teeSheetToast.show([]);

    const formattedDate: string = formatDate(date);

    const params = {
      date: formattedDate,
      module_id: reservationState.module?.id,
      facility_id: reservationState.module?.facility_id,
    };

    let notificationRes: IAPIResponse<any>;

    if (reservationState.activeUser) {
      notificationRes = await GetCustomerReservationNotificationAll(params, token ? false : true, token);
    } else {
      notificationRes = await GetGuestReservationNotificationAll(params, token ? false : true, token);
    }

    if (token && token.reason) {
      return;
    }

    if (notificationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tee sheet notifications")));
      return;
    }

    setReservationState(prevState => ({ ...prevState, notifications: notificationRes.data }));
  }

  function removeNotification(notificationId: number) {
    const notifications = [...reservationState.notifications];
    const index = reservationState.notifications.findIndex(notification => notification.id === notificationId);
    notifications.splice(index, 1);

    setReservationState(prevState => ({ ...prevState, notifications: notifications }));
  }

  return (
    <>
      <ReservationsNavigation
        uiActions={uiActions}
        activeUser={reservationState.activeUser}
        userFirstName={reservationStore?.active_user?.first_name}
      />
      <div className="reservations-flex-row reservations-facility-container">
        <span className="facility-logo">
          {reservationStore?.facility?.logo_source && (
            <img className="facility-logo" src={reservationStore?.facility?.logo_source} alt={t("Facility Logo")} />
          )}
        </span>
        {reservationStore?.facility && (
          <div className="reservations-facility-container-group">
            <p className="facility-name">{reservationStore?.facility?.long_name}</p>
            <p className="facility-address">
              {reservationStore?.facility?.address_line_1}, {reservationStore?.facility?.city},{" "}
              {reservationStore?.facility?.province_name}, {reservationStore?.facility?.postal}
            </p>
          </div>
        )}
      </div>
      <Page title={reservationState?.module?.title}>
        <div className="reservations-date-container">
          <PortalDatePickerInput
            windowWidth={windowSize.width}
            dateBarMinWidth={MOBILE_WIDTH}
            startingDate={reservationState.selectedDate}
            setStartingDate={handleDateChange}
          />
          <div className="reservations-date-bar">
            <DateBar
              numberOfDays={9}
              selectedDate={reservationState.selectedDate}
              onClick={handleDateChange}
              showNavigation={getPortalDateBarShowNavigation(windowSize.width, false)}
            />
          </div>
        </div>

        {reservationState.notifications &&
          reservationState.notifications.map((notification: IReservationNotification, index: number) => {
            return (
              <div key={index} className="mt-2 mb-2">
                <Callout
                  onClose={() => removeNotification(notification.id)}
                  type="warning"
                  title={notification.subject}
                  content={notification.content}
                />
              </div>
            );
          })}

        {reservationState.module?.require_booking_approval && (
          <div className="mt-2 mb-2">
            <Callout
              type="warning"
              title={t("Facility approval required")}
              content={t(
                "This reservation will be submitted as a request. The facility must accept it before it is confirmed.",
              )}
            />
          </div>
        )}

        {reservationState.module &&
        reservationState.locations &&
        reservationState.segments &&
        Object.keys(reservationState.segments)?.length > 0 ? (
          <div className="reservations-segments-container">
            <table className="ui-table ui-table-condensed ui-table-separated-horizontal reservations-segments-table">
              <thead>
                <tr>
                  <td>{t('Time')}</td>
                  {reservationState?.locations?.map((location, locationIndex) => {
                    return <td key={locationIndex}>{location.title}</td>;
                  })}
                </tr>
              </thead>
              <tbody>
                {Object.keys(reservationState?.segments)?.map((segmentKey, segmentKeyIndex) => {
                  return (
                    <tr key={segmentKeyIndex}>
                      <td className="text-bold">{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</td>
                      {reservationState?.locations?.map((location, locationIndex) => {
                        const segment = reservationState?.segments[segmentKey]?.find(
                          searchSegment => searchSegment?.location_id === location?.id,
                        );
                        if (segment?.league_id !== null || segment?.tournament_id !== null) {
                          return (
                            <td
                              key={locationIndex}
                              className={classNames({
                                "segment-league": segment?.league_id !== null,
                                "segment-tournament": segment?.tournament_id !== null,
                              })}
                            >
                              <div className="segment-container">
                                {segment?.league_id && (
                                  <>
                                    <p className="text-semibold">
                                      <span>{location.title}, </span>
                                      <span>{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</span>
                                    </p>
                                    <p>
                                      <span>{t('League')}</span>
                                    </p>
                                  </>
                                )}
                                {segment?.tournament_id && (
                                  <>
                                    <p className="text-semibold">
                                      <span>{location.title}, </span>
                                      <span>{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</span>
                                    </p>
                                    <p>
                                      <span>{t('Tournament')}</span>
                                    </p>
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        } else if (segment?.blocked_type === "open" && segment?.booking_id === null) {
                          return (
                            <td
                              key={locationIndex}
                              className={classNames("segment-available")}
                              onClick={() => checkUserLoggedIn(location, segment)}
                            >
                              <div className="segment-container">
                                <p className="text-semibold">
                                  <span>{location.title}, </span>
                                  <span>{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</span>
                                </p>

                                {reservationState?.module?.display_online_pricing &&
                                segmentAvailabilityPrice(segment?.blocks).amount != null ? (
                                  <p>
                                    {segmentAvailabilityPrice(segment?.blocks).from ? `${t("From")} ` : null}
                                    <LocaleCurrency
                                      amount={segmentAvailabilityPrice(segment?.blocks).amount ?? undefined}
                                    />
                                    {segmentAvailabilityPrice(segment?.blocks).from ? null : " / hour"}
                                  </p>
                                ) : null}
                              </div>
                            </td>
                          );
                        } else if (segment?.blocked_type === "open_reserved") {
                          return (
                            <td key={locationIndex} className="segment-available">
                              <div className="segment-container">
                                <p>{capitalize(segment?.blocked_type)}</p>
                                {segmentAvailabilityPrice(segment?.blocks).amount != null && (
                                  <>
                                    {segmentAvailabilityPrice(segment?.blocks).from ? `${t("From")} ` : null}
                                    <LocaleCurrency
                                      amount={segmentAvailabilityPrice(segment?.blocks).amount ?? undefined}
                                    />
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        } else if (segment?.blocked_type === "blocked" || segment?.blocked_type === "booked") {
                          return (
                            <td key={locationIndex} className="segment-disabled">
                              <div className="segment-container">
                                <p>{capitalize(segment?.blocked_type)}</p>
                              </div>
                            </td>
                          );
                        } else {
                          return <td key={locationIndex} className="segment-unavailable"></td>;
                        }
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Callout type="error" title={t("No Reservations")} content={t("There are no reservations for this day")} />
        )}
      </Page>
    </>
  );
}
